import { Grid, Divider } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { LuUserPlus } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { IWorkAssignmentDTO } from 'wisestep-api';
import {
  WsTypography,
  WsTypoVariants,
  WsButton,
  WsMoreOptionButton,
  IUserDTO,
  WsDrawer,
} from 'wisestep-base';

import { useUserContext } from '../../../context';
import EditJobAdminTeam from '../../../features/jobs/listing/components/viewJd/editJobAdminTeam';
import EditWorkAssignmentTeam from '../../../features/jobs/listing/components/viewJd/editWorkAssignmentTeam';
import WsLink from '../../../features/teams/components/WsLink';
import { useJobCRMContext } from '../context';

const enum JOB_ALLOCATION_DRAWER {
  CHANGE_JOB_ADMIN,
  CHANGE_WORK_ASSIGNMENT_ADMIN,
  NONE,
}

const JobAllocationHeader = ({ accessChangeAdminTeam }: { accessChangeAdminTeam: boolean }) => {
  const { job, workAssignment, getWorkAssignment, getJobDetails } = useJobCRMContext();
  const loggedInUser = useUserContext().userDTO as IUserDTO;

  const navigate = useNavigate();

  const handleAssignRecruiters = () => navigate(`/jobs/${job?.id}/assign`);

  const [openDrawerType, setOpenDrawerType] = useState<JOB_ALLOCATION_DRAWER>(
    JOB_ALLOCATION_DRAWER.NONE
  );

  const isAssigned =
    loggedInUser?.email &&
    job?.assignedTo?.filter((user) => user.recruiterId === loggedInUser.email);

  const accessPermisson =
    job?.canManageJob || job?.createdBy === loggedInUser?.email || isEmpty(isAssigned);

  const finalizedOptions = [
    ...(accessPermisson && accessChangeAdminTeam ? ['Change Job Admin Team'] : []),
    ...(accessPermisson ? ['Change Work Assignment Team'] : []),
  ];

  const openJobAdminDrawer = () => setOpenDrawerType(JOB_ALLOCATION_DRAWER.CHANGE_JOB_ADMIN);
  const openWorkAssigmentAdminDrawer = () =>
    setOpenDrawerType(JOB_ALLOCATION_DRAWER.CHANGE_WORK_ASSIGNMENT_ADMIN);

  const closeDrawer = () => setOpenDrawerType(JOB_ALLOCATION_DRAWER.NONE);

  const moreOptionHandlers = new Map<string, () => void>([
    ['Change Job Admin Team', () => openJobAdminDrawer()],
    ['Change Work Assignment Team', () => openWorkAssigmentAdminDrawer()],
  ]);

  const onMoreOptionClick = (option: string) => {
    const handler = moreOptionHandlers.get(option);
    if (!handler) {
      console.warn(`No handler found for option: ${option}`);
      return;
    }
    handler();
  };
  return (
    <>
      <Grid item display="flex" justifyContent="space-between" alignItems="center">
        <Grid item display="flex" direction="column" rowGap={1}>
          <Grid item display="flex" columnGap={2} alignItems="center" alignContent="center">
            <WsTypography bold variant={WsTypoVariants.SUB_TITLE}>
              Allocation
            </WsTypography>
          </Grid>

          <Grid item display="flex" columnGap={1} alignItems="center" alignContent="center">
            <WsTypography opacity>Job Admin Team - </WsTypography>
            <WsLink
              to={`/team/team-members-view?teamId=${job?.team?.id}&teamName=${job?.team?.name}`}
              sx={{
                fontSize: 14,
                '&:hover': { textDecoration: 'none' },
              }}
            >
              {job?.team?.name ?? 'Not Available'}
            </WsLink>

            <Divider orientation="vertical" flexItem sx={{ height: 24 }} />

            <WsTypography opacity>Work Assignment Team - </WsTypography>
            <WsLink
              to={`/team/team-members-view?teamId=${workAssignment?.team?.id}&teamName=${workAssignment?.team?.name}`}
              sx={{
                fontSize: 14,
                '&:hover': { textDecoration: 'none' },
              }}
            >
              {workAssignment?.team?.name ?? 'Not Available'}
            </WsLink>
          </Grid>
        </Grid>

        <Grid item display="flex" alignItems="center" columnGap={1}>
          {accessPermisson && (
            <WsButton startIcon={<LuUserPlus />} onClick={handleAssignRecruiters}>
              Assign Recruiter
            </WsButton>
          )}

          {finalizedOptions.length && (
            <WsMoreOptionButton onItemClick={onMoreOptionClick} items={finalizedOptions} />
          )}
        </Grid>
      </Grid>
      <WsDrawer
        size="small"
        open={openDrawerType === JOB_ALLOCATION_DRAWER.CHANGE_JOB_ADMIN}
        onClose={closeDrawer}
        title="Edit Job Admin Team"
        style={{ zIndex: 1300 }}
        headerSize="small"
      >
        {job && (
          <EditJobAdminTeam
            job={job}
            closeDrawer={closeDrawer}
            onJobAdminTeamChange={getJobDetails}
          />
        )}
      </WsDrawer>
      <WsDrawer
        size="small"
        open={openDrawerType === JOB_ALLOCATION_DRAWER.CHANGE_WORK_ASSIGNMENT_ADMIN}
        onClose={closeDrawer}
        title="Edit Work Assignment Team"
        style={{ zIndex: 1300 }}
        headerSize="small"
      >
        {workAssignment && (
          <EditWorkAssignmentTeam
            workAssignment={workAssignment as IWorkAssignmentDTO}
            closeDrawer={closeDrawer}
            onWorkAssignmentTeamChange={getWorkAssignment}
          />
        )}
      </WsDrawer>
    </>
  );
};

export default JobAllocationHeader;
